/**
 * Historique opposable du dossier Vérificateur — chronologie complète
 * de tous les événements (création, upload, paiement, transmission, accès spécialiste,
 * consultations, téléchargements) avec niveau d'authentification.
 */
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { History, Loader2, ShieldCheck } from "lucide-react";

const ACTION_LABELS = {
  "verifier.session.created": "Session créée",
  "verifier.document.uploaded": "Document déposé",
  "verifier.report.unlocked": "Rapport débloqué (paiement)",
  "verifier.report.downloaded": "Rapport PDF téléchargé",
  "verifier.handoff.created": "Dossier transmis à un spécialiste",
  "verifier.handoff.accepted": "Dossier accepté par le spécialiste",
  "verifier.handoff.declined": "Dossier décliné par le spécialiste",
  "verifier.handoff.revoked": "Transmission révoquée",
  "verifier.handoff.viewed": "Rapport consulté par le spécialiste",
  "auth.step_up.success": "Authentification renforcée validée",
  "auth.step_up.failed": "Authentification renforcée refusée",
};

const AUTH_BADGES = {
  session: { label: "Session", cls: "bg-slate-100 text-slate-600 border border-slate-200" },
  password: { label: "Mot de passe", cls: "bg-petrol/10 text-petrol border border-petrol/30" },
  step_up: { label: "Réauth. renforcée", cls: "bg-copper/15 text-copper-deep border border-copper/30" },
  "2fa": { label: "2FA", cls: "bg-petrol/15 text-petrol border border-petrol/30" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "medium" }); }
  catch { return iso; }
}

export default function VerifierHistoryPanel({ sessionToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionToken) return;
    setLoading(true);
    apiClient.get(`/verifier/${sessionToken}/history`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Impossible de charger l'historique."))
      .finally(() => setLoading(false));
  }, [sessionToken]);

  if (loading) {
    return (
      <Card className="p-5" data-testid="verifier-history-loading">
        <Loader2 className="w-5 h-5 text-copper animate-spin" />
      </Card>
    );
  }
  if (error) {
    return <Card className="p-5 border-bordeaux/30 text-bordeaux text-sm" data-testid="verifier-history-error">{error}</Card>;
  }
  if (!data) return null;

  const events = data.events || [];

  return (
    <Card className="p-5" data-testid="verifier-history-panel">
      <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1 inline-flex items-center gap-2">
        <History className="w-3.5 h-3.5" /> Historique opposable
      </p>
      <h3 className="font-serif italic text-xl text-night mb-1">
        Historique du dossier ({events.length})
      </h3>
      <p className="text-[11px] text-night/60 italic mb-4">
        Journal infalsifiable. Conservé 2 ans (RGPD). Chaque ligne enregistre l'action,
        l'auteur, la date, l'IP, le navigateur et le niveau d'authentification.
      </p>

      {data.fingerprint && (
        <div className="mb-4 p-2 rounded-sm bg-paper border border-sage text-[11px] font-mono text-night/70 break-all">
          <ShieldCheck className="w-3 h-3 inline mr-1 text-petrol" />
          Empreinte SHA-256 : <span className="text-night">{data.fingerprint}</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-night/55 border-b border-sage">
              <th className="py-2 pr-3 font-medium whitespace-nowrap">Date / Heure</th>
              <th className="py-2 pr-3 font-medium">Action</th>
              <th className="py-2 pr-3 font-medium">Auteur</th>
              <th className="py-2 pr-3 font-medium whitespace-nowrap">Provenance</th>
              <th className="py-2 font-medium whitespace-nowrap">Authentification</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e, i) => {
              const lvl = AUTH_BADGES[e.auth_level] || AUTH_BADGES.session;
              const actor = e.actor_email
                || (e.anonymous_id ? `Anonyme #${e.anonymous_id}` : "—");
              const role = e.actor_role
                ? <span className="text-night/55 ml-1">({e.actor_role})</span>
                : null;
              const ip = e.ip || "—";
              const where = [e.country && e.country !== "-" ? e.country : null, e.ua_browser, e.ua_device]
                .filter(Boolean).join(" · ") || "—";
              return (
                <tr key={i} className="border-b border-sage/50" data-testid={`history-row-${i}`}>
                  <td className="py-2.5 pr-3 align-top text-[11px] text-night/75 whitespace-nowrap">{formatDate(e.created_at)}</td>
                  <td className="py-2.5 pr-3 align-top">
                    <span className="text-night">{ACTION_LABELS[e.action] || e.action}</span>
                    {e.details?.reason && (
                      <span className="block text-[11px] text-night/60 italic mt-0.5">Motif : {e.details.reason}</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 align-top text-[12px]">
                    {actor}{role}
                  </td>
                  <td className="py-2.5 pr-3 align-top text-[11px] text-night/65">
                    <span className="font-mono">{ip}</span>
                    <span className="block text-night/55">{where}</span>
                  </td>
                  <td className="py-2.5 align-top">
                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm ${lvl.cls}`}>
                      {lvl.label}
                    </span>
                  </td>
                </tr>
              );
            })}
            {events.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-night/55 text-sm italic">Aucun événement enregistré.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
